import React, { FC } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ThunkDispatch } from "@reduxjs/toolkit";
import { Box, Button, Drawer, IconButton } from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import { styled, useTheme } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { useTranslation } from "react-i18next";
import { DataTableFiltersStyles } from "../FiltersSideBarStyles";
import SpecieFilter from "./filters/specie/SpecieFilter";
import FilterListOffIcon from "@mui/icons-material/FilterListOff";
import AutocompleteDataFilters from "./autoCompleteFilters/AutoCompleteFilters";

import {
  resetSelectAllActionsFilter,
  resetSelectAllSitesFilter,
  resetSelectAllUsersFilter,
  setDateRangePicker,
  setSelectActions,
  setSelectAllActionsFilter,
  setSelectAllSitesFilter,
  setSelectAllUsersFilter,
  setSelectSites,
  setSelectUsers,
  ValuesSelected,
  initialState,
} from "../../../../../../../redux/slices/dataTable/dataTableFilters";
import {
  setIsOpenFilters,
  setSideBarFiltersVisible,
} from "../../../../../../../redux/slices/dataTable/data";

import DataTableColumns from "../../../../columnsAndActions/ColumnsAndActions";

import {
  selectDataTableUnifiedState,
  selectExportDataState,
} from "../../../../../../../redux/selectors/unifiedSelectors";
import { setTableDataOptions } from "../../../../../../../redux/slices/UnifiedSlices/SharedDataReducer";
import { dataTableActionNewFilter } from "../../../../../../../redux/actions/dataTable/DataTableAsyncActions";

const SetActions = () => {
  const dispatch = useDispatch<ThunkDispatch<any, any, any>>();
  return {
    setSelectAllSitesFilter: (valuesSelected: ValuesSelected[]) => {
      dispatch(setSelectAllSitesFilter(valuesSelected));
    },

    resetSelectAllSitesFilter: () => {
      dispatch(resetSelectAllSitesFilter());
    },

    setSelectSites: (valuesSelected: ValuesSelected[]) => {
      dispatch(setSelectSites(valuesSelected));
    },

    setSelectAllActionsFilter: (valuesSelected: ValuesSelected[]) => {
      dispatch(setSelectAllActionsFilter(valuesSelected));
    },

    resetSelectAllActionsFilter: () => {
      dispatch(resetSelectAllActionsFilter());
    },

    setSelectActions: (valuesSelected: ValuesSelected[]) => {
      dispatch(setSelectActions(valuesSelected));
    },

    setSelectAllUsersFilter: (valuesSelected: ValuesSelected[]) => {
      dispatch(setSelectAllUsersFilter(valuesSelected));
    },

    resetSelectAllUsersFilter: () => {
      dispatch(resetSelectAllUsersFilter());
    },

    setSelectUsers: (valuesSelected: ValuesSelected[]) => {
      dispatch(setSelectUsers(valuesSelected));
    },

    setSideBarFiltersVisible: (value: boolean) => {
      dispatch(setSideBarFiltersVisible(value));
    },
    dataTableActions: () => {
      dispatch(dataTableActionNewFilter());
    },
    resetDataTableActions: () => {
      dispatch(setDateRangePicker(initialState.dataDateActivity));
      dispatch(
        setTableDataOptions({ page: 0, rowOptions: 100, totalRows: null }),
      );
      dispatch(dataTableActionNewFilter());
    },
    setIsOpenFilters: (value: {
      locations: boolean;
      users: boolean;
      actions: boolean;
    }) => {
      dispatch(setIsOpenFilters(value));
    },
  };
};
const SidebarDataFilters: FC = () => {
  const [t] = useTranslation("global");

  const { filtersSideBarVisible, loading, isOpenFilters, filters } =
    useSelector(selectDataTableUnifiedState);

  const {
    dataSBFilterValues,
    locations,
    users,
    loadingFilters,
    actionsFilters,
  } = filters;

  const { loadingExport } = useSelector(selectExportDataState);
  const { columnsDataTable } = DataTableColumns();

  const loadings =
    loading || loadingExport || loadingFilters || columnsDataTable.length === 0;

  const {
    setSelectAllSitesFilter,
    resetSelectAllSitesFilter,
    setSelectSites,
    resetSelectAllUsersFilter,
    setSelectAllUsersFilter,
    setSelectUsers,
    resetSelectAllActionsFilter,
    setSelectActions,
    setSelectAllActionsFilter,
    setSideBarFiltersVisible,
    dataTableActions,
    resetDataTableActions,
    setIsOpenFilters,
  } = SetActions();

  const DrawerHeader = styled("div")(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(0, 1),
    ...theme.mixins.toolbar,
    justifyContent: "flex-end",
  }));

  const filtersInformation = [
    {
      typeOfAutocomplete: "locations",
      dataOptions: dataSBFilterValues.locations,
      allValuesSelected: locations.allValuesSelected,
      valuesSelected: locations.valuesSelected,
    },

    {
      typeOfAutocomplete: "users",
      dataOptions: dataSBFilterValues.users,
      allValuesSelected: users.allValuesSelected,
      valuesSelected: users.valuesSelected,
    },
    {
      typeOfAutocomplete: "actions",
      dataOptions: dataSBFilterValues.actions,
      allValuesSelected: actionsFilters.allValuesSelected,
      valuesSelected: actionsFilters.valuesSelected,
    },
  ];

  function PersistentDrawerLeft() {
    const theme = useTheme();

    const handleDrawerClose = () => {
      setSideBarFiltersVisible(false);
      setIsOpenFilters({
        actions: false,
        users: false,
        locations: false,
      });
    };

    return (
      <Box sx={DataTableFiltersStyles.SideBar.Box}>
        <CssBaseline />

        <Drawer
          onClose={handleDrawerClose}
          sx={DataTableFiltersStyles.SideBar.Drawer}
          variant="persistent"
          anchor="left"
          open={filtersSideBarVisible}
        >
          <DrawerHeader>
            <div className="title-container-sb">
              <h4>{t("Data_sidebar.Add_filter")}</h4>
            </div>

            <IconButton onClick={handleDrawerClose}>
              {theme.direction === "ltr" ? (
                <ChevronLeftIcon />
              ) : (
                <ChevronRightIcon />
              )}
            </IconButton>
          </DrawerHeader>

          <div
            onClick={() => {
              setIsOpenFilters({
                actions: false,
                users: false,
                locations: false,
              });
            }}
            className="data-extra-filters-container"
          >
            <div className="species-container mb-1">
              <div>
                <span className="titles-sb-features">{t("Data.Species")}</span>
              </div>
              <SpecieFilter loading={loadings} />
            </div>

            <div className="Autocomplete-filters-container">
              {filtersInformation.map((v) => {
                const managingSelectAll = () => {
                  if (v.typeOfAutocomplete === "locations") {
                    return setSelectAllSitesFilter;
                  }
                  if (v.typeOfAutocomplete === "actions") {
                    return setSelectAllActionsFilter;
                  }
                  if (v.typeOfAutocomplete === "users") {
                    return setSelectAllUsersFilter;
                  }
                };

                const managingResetSelectAll = () => {
                  if (v.typeOfAutocomplete === "locations") {
                    return resetSelectAllSitesFilter;
                  }
                  if (v.typeOfAutocomplete === "actions") {
                    return resetSelectAllActionsFilter;
                  }
                  if (v.typeOfAutocomplete === "users") {
                    return resetSelectAllUsersFilter;
                  }
                };

                const managingSelectOption = () => {
                  if (v.typeOfAutocomplete === "locations") {
                    return setSelectSites;
                  }
                  if (v.typeOfAutocomplete === "actions") {
                    return setSelectActions;
                  }
                  if (v.typeOfAutocomplete === "users") {
                    return setSelectUsers;
                  }
                };

                return AutocompleteDataFilters(
                  v.typeOfAutocomplete,
                  v.dataOptions as ValuesSelected[],
                  v.allValuesSelected,
                  v.valuesSelected,
                  loadings,
                  managingSelectAll(),
                  managingResetSelectAll(),
                  managingSelectOption(),
                  setIsOpenFilters,
                  isOpenFilters,
                );
              })}
            </div>

            <div className="button-position-sb-data-filters">
              <div className="buttons-actions-data-sb">
                <Button
                  sx={DataTableFiltersStyles.SideBar.Button}
                  variant="outlined"
                  className="mt-3"
                  disabled={loading}
                  onClick={() => resetDataTableActions()}
                  startIcon={<FilterListOffIcon />}
                >
                  {t("Common_options.Reset_filters")}
                </Button>
              </div>
              <div className="buttons-actions-data-sb">
                <Button
                  variant="contained"
                  disabled={loading}
                  onClick={() => dataTableActions()}
                  className="mt-3"
                  sx={DataTableFiltersStyles.SideBar.Button}
                >
                  {t("Common_options.Apply")}
                </Button>
              </div>
            </div>
          </div>
        </Drawer>
      </Box>
    );
  }

  return PersistentDrawerLeft();
};

export default SidebarDataFilters;
